import {
  OAUTH_CLIENT_ID,
  OAUTH_REDIRECT_URI,
  OAUTH_SCOPES,
  TWENTY_BASE_URL,
} from './config';

// OAuth 2.1 authorization-code + PKCE against Twenty's auth server, run through
// the Office Dialog API (the taskpane iframe can't host a cross-origin login).
// Acts AS THE USER, so logged emails are attributed to whoever is signed in.
// Verified end-to-end in the Phase 0 spike: 30-min access tokens + refresh.

const REFRESH_TOKEN_KEY = 'clsCrmRefreshToken';

const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const randomString = (byteLength: number): string =>
  base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)));

const challengeFromVerifier = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
};

// roamingSettings persists per-user/per-add-in and roams across the user's
// Office installs - the right place for a refresh token.
const saveRefreshToken = (token: string): Promise<void> =>
  new Promise((resolve) => {
    const settings = Office.context.roamingSettings;
    settings.set(REFRESH_TOKEN_KEY, token);
    settings.saveAsync(() => resolve());
  });

const loadRefreshToken = (): string | null =>
  (Office.context.roamingSettings.get(REFRESH_TOKEN_KEY) as string) ?? null;

type TokenResponse = {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
};

const requestToken = async (
  params: Record<string, string>,
): Promise<TokenResponse> => {
  const response = await fetch(`${TWENTY_BASE_URL}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(params).toString(),
  });
  if (!response.ok) {
    throw new Error(`Token endpoint returned ${response.status}`);
  }
  return (await response.json()) as TokenResponse;
};

const interactiveLogin = async (): Promise<TokenResponse> => {
  const verifier = randomString(40);
  const challenge = await challengeFromVerifier(verifier);
  const state = randomString(12);
  const authorizeUrl =
    `${TWENTY_BASE_URL}/authorize?` +
    new URLSearchParams({
      response_type: 'code',
      client_id: OAUTH_CLIENT_ID,
      redirect_uri: OAUTH_REDIRECT_URI,
      scope: OAUTH_SCOPES,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
    }).toString();

  const code = await new Promise<string>((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      authorizeUrl,
      { height: 60, width: 30, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(asyncResult.error);
          return;
        }
        const dialog = asyncResult.value;
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg) => {
            dialog.close();
            try {
              const message = JSON.parse(
                (arg as { message: string }).message,
              ) as { code?: string; state?: string; error?: string };
              if (message.state !== state) {
                reject(new Error('OAuth state mismatch'));
              } else if (message.code) {
                resolve(message.code);
              } else {
                reject(new Error(message.error ?? 'Authorization failed'));
              }
            } catch (parseError) {
              reject(parseError as Error);
            }
          },
        );
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () =>
          reject(new Error('Sign-in was cancelled')),
        );
      },
    );
  });

  const tokens = await requestToken({
    grant_type: 'authorization_code',
    code,
    redirect_uri: OAUTH_REDIRECT_URI,
    client_id: OAUTH_CLIENT_ID,
    code_verifier: verifier,
  });
  if (tokens.refresh_token) await saveRefreshToken(tokens.refresh_token);
  return tokens;
};

let cachedAccess: { token: string; expiresAt: number } | null = null;

// Returns a valid access token: cached -> silent refresh -> interactive login.
export const getAccessToken = async (
  forceInteractive = false,
): Promise<string> => {
  const now = Date.now();
  if (
    !forceInteractive &&
    cachedAccess &&
    cachedAccess.expiresAt - 60_000 > now
  ) {
    return cachedAccess.token;
  }

  if (!forceInteractive) {
    const refreshToken = loadRefreshToken();
    if (refreshToken) {
      try {
        const tokens = await requestToken({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: OAUTH_CLIENT_ID,
        });
        if (tokens.refresh_token) await saveRefreshToken(tokens.refresh_token);
        cachedAccess = {
          token: tokens.access_token,
          expiresAt: Date.now() + tokens.expires_in * 1000,
        };
        return cachedAccess.token;
      } catch {
        // refresh failed (revoked/expired) - fall through to interactive
      }
    }
  }

  const tokens = await interactiveLogin();
  cachedAccess = {
    token: tokens.access_token,
    expiresAt: Date.now() + tokens.expires_in * 1000,
  };
  return cachedAccess.token;
};

export const isLoggedIn = (): boolean => loadRefreshToken() !== null;
